import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SuccessModal from "../utils/SuccessModal";
import ErrorModal from "../utils/ErrorModal";
import CountrySelect from "../utils/countrySelect";
import {
  getCountryCallingCode,
  parsePhoneNumber,
} from "react-phone-number-input";
import { uploadFile } from "../utils/uploadToS3";
import { useOrganization } from "../utils/OrganizationContext";
import NotificationWarning from "../utils/NotificationWarning";
import ThemedDatePicker from "../utils/ThemedDatePicker";
import ThemedSelect from "../utils/ThemedSelect";

/* ---------- Direction C tokens (styling only) — same as Add lead ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#DDE4EC";
const FIELD_BG = "#F8FAFC";
const PRIMARY = "#0B5C8A";
const PRIMARY_DARK = "#084767";
const ERROR = "#C42B24";

const SEC_COLORS = {
  unit: "#12A08F",
  commercials: "#E0911B",
  location: "#2F7FD1",
};

const formTheme = createTheme({
  palette: {
    primary: { main: PRIMARY, dark: PRIMARY_DARK, contrastText: "#FFFFFF" },
    error: { main: ERROR },
    text: { primary: INK, secondary: "#4A5A6E" },
  },
  shape: { borderRadius: 8 },
  typography: { fontFamily: FONT, button: { textTransform: "none", fontWeight: 600 } },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          background: FIELD_BG,
          fontFamily: FONT,
          fontSize: 13.5,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: FIELD_EDGE },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#B4C6DA" },
          "&.Mui-focused": { background: "#FFFFFF" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: PRIMARY, borderWidth: 1 },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: ERROR },
        },
        input: { "&::placeholder": { color: "#8A97A8", opacity: 1 } },
      },
    },
    MuiFormHelperText: { styleOverrides: { root: { display: "none" } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: 13.5,
          borderRadius: 8,
          minHeight: 36,
          "&.Mui-selected, &.Mui-selected:hover": { background: "#EAF2FB" },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { marginTop: 4, borderRadius: 12, border: `1px solid ${LINE}`, boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)" },
        option: { fontFamily: FONT, fontSize: 13.5, borderRadius: 8, marginInline: 6 },
      },
    },
  },
});

/* ---------- shared visual styles (values only — no logic) ---------- */
const card = {
  background: "#FFFFFF",
  border: `1px solid ${LINE}`,
  borderRadius: 14,
  marginBottom: 14,
  scrollMarginTop: 90,
};
const cardHead = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "13px 16px",
  borderBottom: `1px solid ${LINE}`,
};
const cardTitle = {
  display: "flex",
  alignItems: "center",
  gap: 9,
  fontFamily: FONT,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.11em",
  textTransform: "uppercase",
  color: INK_2,
};
const dot = (c, size = 6) => ({
  width: size,
  height: size,
  flex: `0 0 ${size}px`,
  borderRadius: "50%",
  background: c,
});
const cardMeta = { fontFamily: FONT, fontSize: 12, color: MUTED };
const lbl = {
  display: "block",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  marginBottom: 6,
  fontFamily: FONT,
  fontSize: 12,
  fontWeight: 600,
  color: INK,
};
const req = { color: ERROR, marginLeft: 3 };
const inp = (err) => ({
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  height: 36,
  padding: "0 12px",
  margin: 0,
  background: FIELD_BG,
  border: `1px solid ${err ? ERROR : FIELD_EDGE}`,
  borderRadius: 8,
  fontFamily: FONT,
  fontSize: 13.5,
  color: INK,
  outline: "none",
});
const errText = {
  display: "block",
  margin: "4px 0 0",
  fontFamily: FONT,
  fontSize: 11.5,
  fontWeight: 500,
  color: ERROR,
};
const acSx = {
  margin: 0,
  "& .MuiInputBase-root": {
    minHeight: 36,
    fontSize: "13.5px",
    borderRadius: "8px",
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    paddingLeft: "8px !important",
  },
  "& .MuiInputBase-input": { padding: "7px 4px !important", minWidth: "0 !important" },
};
const boxWrap = (err) => ({
  display: "flex",
  alignItems: "center",
  height: 36,
  boxSizing: "border-box",
  background: FIELD_BG,
  border: `1px solid ${err ? ERROR : FIELD_EDGE}`,
  borderRadius: 8,
  overflow: "hidden",
});
const bareInp = {
  flex: 1,
  minWidth: 0,
  height: "100%",
  boxSizing: "border-box",
  padding: "0 12px 0 10px",
  margin: 0,
  border: 0,
  background: "transparent",
  fontFamily: FONT,
  fontSize: 13.5,
  color: INK,
  outline: "none",
};
const primaryBtn = {
  height: 38,
  px: 2,
  margin: "0 !important",
  borderRadius: "10px !important",
  background: `${PRIMARY} !important`,
  color: "#FFFFFF !important",
  border: "0 !important",
  fontFamily: FONT,
  fontSize: 14,
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "none",
  whiteSpace: "nowrap",
  "&:hover": { background: `${PRIMARY_DARK} !important` },
  "&.Mui-disabled": { opacity: 0.6 },
};
const ghostBtn = {
  height: 38,
  px: 2,
  margin: "0 !important",
  borderRadius: "10px !important",
  background: "#FFFFFF !important",
  color: `${INK} !important`,
  border: `1px solid ${FIELD_EDGE} !important`,
  fontFamily: FONT,
  fontSize: 14,
  fontWeight: 600,
  textTransform: "none",
  "&:hover": { background: "#F4F7FA !important" },
};
const tile = {
  position: "relative",
  borderRadius: "12px",
  overflow: "hidden",
  border: `1px solid ${LINE}`,
  background: "#F7F9FC",
};
const removeBtn = {
  position: "absolute",
  top: 6,
  right: 6,
  minWidth: 0,
  height: 24,
  px: 1,
  py: 0,
  fontFamily: FONT,
  fontSize: 11.5,
  fontWeight: 700,
  textTransform: "none",
  borderRadius: "7px",
  background: "rgba(255,255,255,0.94)",
  color: "#A6342A",
  boxShadow: "0 2px 6px rgba(15,27,42,0.2)",
  "&:hover": { background: "#FFFFFF" },
};
const miniHead = { mb: 1, fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: MUTED };
const cols = (n) => ({
  display: "grid",
  gap: "16px 12px",
  gridTemplateColumns: {
    xs: "minmax(0,1fr)",
    sm: "repeat(2, minmax(0,1fr))",
    lg: `repeat(${n}, minmax(0,1fr))`,
  },
});

// Scoped resets so global App.css rules can't squeeze or restyle this page.
const SCOPED_CSS = `
.dc-inv .dc-card{width:100%!important;max-width:none!important;margin:0 0 14px 0!important;padding:0!important;box-shadow:none!important;box-sizing:border-box!important;}
.dc-inv .dc-card label{width:auto!important;margin-top:0!important;font-size:12px!important;line-height:1.4!important;}
.dc-inv .dc-card input:not([type=checkbox]):not([type=file]),.dc-inv .dc-card textarea{width:100%!important;max-width:none!important;margin:0!important;box-sizing:border-box!important;font-size:13.5px!important;font-family:'Plus Jakarta Sans',system-ui,sans-serif!important;}
.dc-inv .dc-card input:not([type=checkbox]):not([type=file]){height:36px!important;}
.dc-inv .dc-card .MuiInputBase-root input{height:auto!important;}
.dc-inv .dc-card input.dc-bare{height:100%!important;border:0!important;background:transparent!important;flex:1 1 auto;min-width:0;}
.dc-inv .dc-card input::placeholder,.dc-inv .dc-card textarea::placeholder{color:#8A97A8;opacity:1;}
.dc-inv .dc-cc *{font-family:'Plus Jakarta Sans',system-ui,sans-serif!important;font-size:13.5px!important;color:#0F1B2A;}
.dc-inv .dc-card .MuiAutocomplete-root,.dc-inv .dc-card .MuiInputBase-root{width:100%!important;max-width:none!important;}
`;

const REQUIRED_FIELDS = [
  "projectId",
  "type",
  "size",
  "status",
  "floor",
  "totalFloor",
  "parking",
  "balcony",
  "bedroom",
  "bath",
  "possessionDate",
  "price",
  "rent",
  "age",
  "address",
];

// Which fields live in which card (display grouping only)
const UNIT_KEYS = ["projectId", "type", "status", "building", "flat", "floor", "totalFloor", "furnished", "size", "bedroom", "bath", "balcony", "parking", "possessionDate", "age"];
const COMMERCIAL_KEYS = ["price", "isNegotiable", "rent", "deposit"];
const LOCATION_KEYS = ["address", "conditions", "ownerName", "ownerContact", "googleMap", "media"];

const AddInventory = ({ onSuccess }) => {
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  // const [role, setRole] = useState();
  const [country, setCountry] = useState("IN");
  const [form, setForm] = useState({
    projectId: JSON.parse(localStorage.getItem("projectId")) || "",
    type: "",
    size: "",
    status: "",
    floor: "",
    totalFloor: "",
    parking: "",
    balcony: "",
    bedroom: "",
    bath: "",
    possessionDate: "",
    price: "",
    rent: "",
    age: "",
    address: "",
    building: "",
    flat: "",
    deposit: "",
    furnished: "",
    conditions: "",
    isNegotiable: "",
    ownerName: JSON.parse(localStorage.getItem("leadData"))?.name || "",
    ownerContact: "",
    googleMap: "",
    leadId: JSON.parse(localStorage.getItem("leadData"))?.leadId || "",
    media: [],
  });
  const [success, setSuccess] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errors, setErrors] = useState({});
  const orgId = JSON.parse(localStorage.getItem("org"));
  const { role } = useOrganization();
  const isMobile = useMediaQuery("(max-width: 750px)");
  const narrow = useMediaQuery("(max-width: 1000px)");
  // Inside the Add lead popup (onSuccess passed) there's no room for the side nav
  const hideSectionNav = narrow || !!onSuccess;
  const sectionRefs = useRef({});
  const [activeSection, setActiveSection] = useState("unit");

  const parseStoredPhone = (phoneWithCode) => {
    if (!phoneWithCode) return { country: "IN", phone: "" };
    try {
      const normalized = phoneWithCode.startsWith("+")
        ? phoneWithCode
        : `+${phoneWithCode}`;
      const parsed = parsePhoneNumber(normalized);
      return {
        country: parsed.country || "IN", // "IN", "US"
        phone: parsed.nationalNumber, // "9876543210"
      };
    } catch (e) {
      return { country: "IN", phone: phoneWithCode };
    }
  };

  useEffect(() => {
    if (!storedUser?.accessToken) return;

    // const decoded = jwtDecode(storedUser.accessToken);
    // const role = decoded.userRole?.[0];
    // setRole(role);
    if (JSON.parse(localStorage.getItem("leadData"))) {
      setForm((prev) => ({
        ...prev,
        ownerContact: parseStoredPhone(
          JSON.parse(localStorage.getItem("leadData")).phone,
        ).phone,
      }));
      setCountry(
        parseStoredPhone(JSON.parse(localStorage.getItem("leadData")).phone)
          .country,
      );
    }
    fetchProjects(role);
  }, [orgId]);

  const fetchProjects = async (role) => {
    setLoading(true);
    try {
      const url =
        role === "ADMIN"
          ? `${process.env.REACT_APP_BACKEND_URL}/project/all?orgId=${orgId}`
          : `${process.env.REACT_APP_BACKEND_URL}/project/team/all?orgId=${orgId}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${storedUser.accessToken}` },
      });

      setProjects(res.data || []);
    } catch (err) {
      console.error("Failed to fetch projects", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const submitInventory = async () => {
    const newErrors = {};

    REQUIRED_FIELDS.forEach((field) => {
      if (!form[field] || form[field].toString().trim() === "") {
        newErrors[field] = "This field is required";
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;
    try {
      setBtnLoading(true);
      let uploadedUrl = [];
      if (form.media.length > 0) {
        uploadedUrl = await Promise.all(
          form.media.map((file) => uploadFile(file, "inventory")),
        );
      }
      const payload = {
        projectId: form.projectId,
        type: form.type,
        size: form.size ? Number(form.size) : null,
        status: form.status,
        floor: form.floor ? Number(form.floor) : null,
        totalFloor: form.totalFloor ? Number(form.totalFloor) : null,
        parking: form.parking ? Number(form.parking) : null,
        balcony: form.balcony ? Number(form.balcony) : null,
        bedroom: form.bedroom ? Number(form.bedroom) : null,
        bath: form.bath ? Number(form.bath) : null,
        possessionDate: form.possessionDate,
        price: form.price ? Number(form.price) : null,
        rent: form.rent ? Number(form.rent) : null,
        age: form.age ? Number(form.age) : null,
        address: form.address,
        building: form.building,
        flat: form.flat,
        deposit: form.deposit,
        furnished: form.furnished,
        conditions: form.conditions,
        isNegotiable: form.isNegotiable,
        ownerName: form.ownerName,
        ownerContact: form.ownerContact,
        countryCode: getCountryCallingCode(country),
        googleMap: form.googleMap,
        media: uploadedUrl,
      };

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/inventory?leadId=${form.leadId}`,
        payload,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );

      setSuccess(true);
      resetForm();
      const leadData = JSON.parse(localStorage.getItem("leadData"));

      if (leadData?.leadId) {
        navigate(
          role === "ADMIN"
            ? `/dashboard/lead/${leadData.leadId}`
            : `/dashboard/lead/${leadData.leadId}`,
        );

        localStorage.removeItem("leadData");
      } else if (leadData) {
        localStorage.removeItem("leadData");

        if (onSuccess) {
          onSuccess();
          return;
        }
      } else {
        if (role === "ADMIN") navigate("/dashboard/inventory");
        else navigate("/dashboard/inventory");
      }
    } catch (err) {
      console.error(err);
      setOpenError(true);
    } finally {
      setBtnLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      type: "",
      size: "",
      status: "",
      floor: "",
      totalFloor: "",
      parking: "",
      balcony: "",
      bedroom: "",
      bath: "",
      possessionDate: "",
      price: "",
      rent: "",
      age: "",
      address: "",
      building: "",
      flat: "",
      deposit: "",
      furnished: "",
      conditions: "",
      isNegotiable: "",
      ownerName: "",
      ownerContact: "",
      googleMap: "",
      media: [],
    });
  };

  const handleBack = () => {
    localStorage.removeItem("leadData");
    navigate(`/dashboard/inventory`);
  };

  /* ---------- markup helpers (same names, handlers and errors as before) ---------- */
  const fieldBox = (key, label, required, control, full) => (
    <Box key={key} sx={full ? { gridColumn: "1 / -1" } : undefined}>
      <label style={lbl}>
        {label}
        {required && <span style={req}>*</span>}
      </label>
      {control}
      {errors[key] && (
        <span className="error-text" style={errText}>
          {errors[key]}
        </span>
      )}
    </Box>
  );

  const field = (name, label, placeholder, opts = {}) => {
    const required = REQUIRED_FIELDS.includes(name);
    return fieldBox(
      name,
      label,
      required,
      <input
        className={required ? `${errors[name] ? "input-error" : ""}` : undefined}
        type={opts.type}
        name={name}
        value={form[name]}
        onChange={handleChange}
        placeholder={placeholder}
        style={inp(!!errors[name])}
      />,
      opts.full,
    );
  };

  const projectField = () =>
    fieldBox(
      "projectId",
      "Project",
      true,
      <Autocomplete
        className={`${errors.projectId ? "input-error" : ""}`}
        options={projects || []}
        getOptionLabel={(option) => option.projectName || ""}
        value={projects?.find((p) => p.projectId === form.projectId) || null}
        onChange={(e, value) => {
          setForm((prev) => ({
            ...prev,
            projectId: value?.projectId || "",
          }));
        }}
        isOptionEqualToValue={(option, value) =>
          option.projectId === value.projectId
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select project"
            error={!!errors.projectId}
            helperText={errors.projectId}
          />
        )}
        sx={acSx}
      />,
    );

  const possessionField = () =>
    fieldBox(
      "possessionDate",
      "Possession Date",
      true,
      <ThemedDatePicker
        name="possessionDate"
        value={form.possessionDate}
        onChange={handleChange}
        error={!!errors.possessionDate}
      />,
    );

  const negotiableField = () =>
    fieldBox(
      "isNegotiable",
      "Price Fixed or Negotiable",
      false,
      <ThemedSelect
        name="isNegotiable"
        value={form.isNegotiable}
        onChange={handleChange}
        placeholder="--Choose--"
        options={[
          { value: "", label: "--Choose--" },
          { value: "Fixed", label: "Fixed" },
          { value: "Negotiable", label: "Negotiable" },
        ]}
      />,
    );

  const addressField = () =>
    fieldBox(
      "address",
      "Address",
      true,
      <textarea
        className={`${errors.address ? "input-error" : ""}`}
        name="address"
        rows={3}
        value={form.address}
        onChange={handleChange}
        placeholder="Enter full property address"
        style={{
          ...inp(!!errors.address),
          height: "auto",
          minHeight: 72,
          padding: "9px 12px",
          lineHeight: 1.5,
          resize: "vertical",
        }}
      />,
      true,
    );

  const ownerContactField = () =>
    fieldBox(
      "ownerContact",
      "Property Owner Contact",
      false,
      <div style={boxWrap(false)}>
        <div
          className="dc-cc"
          style={{
            display: "flex",
            alignItems: "center",
            flex: "0 0 auto",
            height: "100%",
            padding: "0 6px 0 10px",
            borderRight: `1px solid ${FIELD_EDGE}`,
            boxSizing: "border-box",
          }}
        >
          <CountrySelect value={country} onChange={setCountry} />
        </div>
        <input
          type="tel"
          name="ownerContact"
          className="dc-bare"
          placeholder="Enter phone number"
          value={form.ownerContact}
          onChange={handleChange}
          style={bareInp}
        />
      </div>,
    );

  const mediaField = () => (
    <Box key="media" sx={{ gridColumn: "1 / -1" }}>
      <label style={lbl}>Inventory Media</label>
      <input
        type="file"
        multiple
        accept="image/*,video/*"
        onChange={(e) => {
          setForm((prev) => ({
            ...prev,
            media: Array.from(e.target.files),
          }));
        }}
        style={{
          display: "block",
          width: "100%",
          boxSizing: "border-box",
          padding: "9px 12px",
          margin: "0 0 12px",
          background: FIELD_BG,
          border: `1px dashed ${FIELD_EDGE}`,
          borderRadius: 8,
          fontFamily: FONT,
          fontSize: 13,
          color: "#4A5A6E",
          cursor: "pointer",
        }}
      />

      {form.media?.length > 0 && (
        <>
          <Typography sx={miniHead}>New Uploads</Typography>

          <Box
            sx={{
              display: "grid",
              gap: 1.25,
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
            }}
          >
            {form.media.map((file, index) => {
              const isVideo = file.type.startsWith("video");

              return (
                <Box sx={tile} key={index}>
                  {isVideo ? (
                    <video
                      src={URL.createObjectURL(file)}
                      controls
                      style={{ width: "100%", height: 100, objectFit: "cover", display: "block" }}
                    />
                  ) : (
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      style={{ width: "100%", height: 100, objectFit: "cover", display: "block" }}
                    />
                  )}

                  <Button
                    size="small"
                    color="error"
                    variant="contained"
                    onClick={() => {
                      setForm((prev) => ({
                        ...prev,
                        media: prev.media.filter((_, i) => i !== index),
                      }));
                    }}
                    sx={removeBtn}
                  >
                    Remove
                  </Button>

                  <Box sx={{ p: 1, bgcolor: "#fff", borderTop: `1px solid ${LINE}` }}>
                    <Typography noWrap sx={{ fontFamily: MONO, fontSize: 11.5, color: "#4A5A6E" }}>
                      {file.name}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );

  /* ---------- sections ---------- */
  const reqCount = (keys) => keys.filter((k) => REQUIRED_FIELDS.includes(k)).length;
  const metaFor = (keys) => (reqCount(keys) ? `${reqCount(keys)} required` : "optional");

  const sections = [
    {
      id: "unit",
      title: "Unit",
      keys: UNIT_KEYS,
      items: [
        projectField(),
        field("type", "Property Type", "Flat / Shop / Office"),
        field("status", "Status", "Available / Sold / Rented"),
        field("building", "Tower Number/Building Number", "Enter tower or building"),
        field("flat", "Flat Number", "Enter flat number"),
        field("floor", "Floor", "Floor number", { type: "number" }),
        field("totalFloor", "Total Floors", "Total floors in building", { type: "number" }),
        field("furnished", "Furnished Type", "Fully / Semi / Unfurnished"),
        field("size", "Size (sqft)", "Enter size in sqft", { type: "number" }),
        field("bedroom", "Bedrooms", "Number of bedrooms", { type: "number" }),
        field("bath", "Bathrooms", "Number of bathrooms", { type: "number" }),
        field("balcony", "Balcony", "Number of balconies", { type: "number" }),
        field("parking", "Parking", "Number of parking slots", { type: "number" }),
        possessionField(),
        field("age", "Property Age (Years)", "Property age in years", { type: "number" }),
      ],
    },
    {
      id: "commercials",
      title: "Commercials",
      keys: COMMERCIAL_KEYS,
      items: [
        field("price", "Property Price", "Enter property price", { type: "number" }),
        negotiableField(),
        field("rent", "Rent", "Monthly rent amount", { type: "number" }),
        field("deposit", "Deposit Amount", "Enter deposit amount", { type: "number" }),
      ],
    },
    {
      id: "location",
      title: "Location & contact",
      keys: LOCATION_KEYS,
      items: [
        addressField(),
        field("ownerName", "Property Owner Name", "Property owner name"),
        ownerContactField(),
        field("googleMap", "Google Maps Link", "Paste Google Maps link"),
        field("conditions", "Any Conditions from owner/seller", "Any conditions from owner/seller"),
        mediaField(),
      ],
    },
  ];

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) setActiveSection(en.target.dataset.sec);
        });
      },
      { rootMargin: "-25% 0px -65% 0px" },
    );
    Object.values(sectionRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollToSection = (id) => {
    setActiveSection(id);
    sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const renderSection = (s) => (
    <section
      key={s.id}
      data-sec={s.id}
      ref={(el) => (sectionRefs.current[s.id] = el)}
      className="dc-card"
      style={card}
    >
      <div style={cardHead}>
        <span style={cardTitle}>
          <span style={dot(SEC_COLORS[s.id])} />
          {s.title}
        </span>
        <span style={cardMeta}>{metaFor(s.keys)}</span>
      </div>
      <Box sx={{ p: "16px 16px 18px" }}>
        <Box sx={cols(5)}>{s.items}</Box>
      </Box>
    </section>
  );

  const sectionNav = (
    <nav
      style={{
        position: "sticky",
        top: 16,
        background: "#FFFFFF",
        border: `1px solid ${LINE}`,
        borderRadius: 14,
        padding: 8,
      }}
    >
      {sections.map((s) => {
        const on = activeSection === s.id;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => scrollToSection(s.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              width: "100%",
              height: 36,
              padding: "0 10px",
              marginBottom: 2,
              border: 0,
              borderRadius: 9,
              background: on ? "#EAF2FB" : "transparent",
              cursor: "pointer",
              fontFamily: FONT,
              fontSize: 13.5,
              fontWeight: on ? 700 : 500,
              color: INK,
              textAlign: "left",
            }}
          >
            <span style={dot(SEC_COLORS[s.id], 7)} />
            <span style={{ flex: 1 }}>{s.title}</span>
            <span style={{ fontFamily: MONO, fontSize: 11, color: MUTED }}>
              {s.items.length}
            </span>
          </button>
        );
      })}
    </nav>
  );

  const selectedProject = projects?.find((p) => p.projectId === form.projectId);

  const cancelButton = (
    <Button className="btn-outlined" onClick={handleBack} sx={ghostBtn}>
      {isMobile && <ArrowBackIcon sx={{ fontSize: 17, mr: 0.5 }} />}
      Cancel
    </Button>
  );

  const saveButton = (
    <Button
      className="btn-contained"
      onClick={submitInventory}
      disabled={btnLoading}
      sx={primaryBtn}
    >
      {btnLoading ? "Saving..." : "Save unit"}
    </Button>
  );

  return (
    <ThemeProvider theme={formTheme}>
      <style>{SCOPED_CSS}</style>
      <div
        className="dc-inv"
        style={{
          fontFamily: FONT,
          color: INK,
          textAlign: "left",
          padding: onSuccess ? "20px 20px 8px" : isMobile ? "4px 0 88px" : "4px 0 32px",
        }}
      >
        <NotificationWarning message="Notifications are off. Enable them from your profile menu to receive inventory alerts." />

        {/* ---------------- PAGE HEADER ---------------- */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 2,
            mb: 2.5,
          }}
        >
          <div style={{ minWidth: 0 }}>
            {!onSuccess && (
              <button
                type="button"
                onClick={handleBack}
                style={{
                  border: 0,
                  background: "transparent",
                  padding: 0,
                  marginBottom: 14,
                  cursor: "pointer",
                  fontFamily: FONT,
                  fontSize: 13,
                  fontWeight: 600,
                  color: PRIMARY,
                }}
              >
                ← Inventory
              </button>
            )}
            <h1
              style={{
                margin: 0,
                fontFamily: FONT,
                fontSize: isMobile ? 24 : 28,
                fontWeight: 800,
                letterSpacing: "-0.03em",
                lineHeight: 1.15,
                color: INK,
              }}
            >
              Add inventory
            </h1>
            {selectedProject?.projectName && (
              <div style={{ marginTop: 6, fontSize: 13.5, color: INK_2 }}>
                {selectedProject.projectName}
              </div>
            )}
          </div>
          {!isMobile && (
            <Box sx={{ display: "flex", gap: 1.25, flex: "0 0 auto" }}>
              {cancelButton}
              {saveButton}
            </Box>
          )}
        </Box>

        {/* ---------------- NAV + SECTIONS ---------------- */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: hideSectionNav
              ? "minmax(0,1fr)"
              : "200px minmax(0,1fr)",
            gap: "14px",
            alignItems: "start",
          }}
        >
          {!hideSectionNav && sectionNav}
          <div>{sections.map(renderSection)}</div>
        </Box>

        {/* ---------------- MOBILE ACTION BAR ---------------- */}
        {isMobile && (
          <Box
            className="btn-container"
            sx={{
              position: onSuccess ? "sticky" : "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 30,
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              p: "10px 16px",
              background: "#FFFFFF",
              borderTop: `1px solid ${LINE}`,
              boxShadow: "0 -8px 24px -16px rgba(15,27,42,0.3)",
            }}
          >
            {cancelButton}
            <Box sx={{ flex: 1, display: "flex", "& > *": { flex: 1 } }}>
              {saveButton}
            </Box>
          </Box>
        )}

        <SuccessModal
          open={success}
          handleClose={() => setSuccess(false)}
          msg={"Inventory added successfully"}
        />
        <ErrorModal open={openError} handleClose={() => setOpenError(false)} />
      </div>
    </ThemeProvider>
  );
};

export default AddInventory;